import React, { useState, useEffect } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, addDays } from 'date-fns';
import { FiChevronLeft, FiChevronRight, FiRefreshCw, FiInfo } from 'react-icons/fi';
import Sidebar from '../../components/Admin/Sidebar';
import SafeIcon from '../../common/SafeIcon';
import ncb from '../../db/ncb';

const AvailabilityManagement = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadBlockedDates = () => {
    setLoading(true);
    const data = ncb.availability.list();
    setBlockedDates(data.map(d => d.date));
    setLoading(false);
  };

  useEffect(() => {
    loadBlockedDates();
  }, []);

  const toggleDate = (dateStr) => {
    ncb.availability.toggle(dateStr);
    loadBlockedDates();
  };

  const renderHeader = () => (
    <div className="flex items-center justify-between mb-8">
      <div>
        <h1 className="font-serif text-3xl text-brand-dark font-bold">Availability Calendar</h1>
        <p className="text-brand-sage text-sm italic">Click on dates to toggle between Booked and Available.</p>
      </div>
      <div className="flex items-center gap-4">
        <button 
          onClick={loadBlockedDates}
          className="p-3 text-brand-olive hover:bg-brand-olive/10 rounded-full transition-all"
        >
          <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
        </button>
        <div className="flex items-center bg-white border border-brand-light/30 rounded-sm overflow-hidden shadow-sm">
          <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-3 hover:bg-brand-cream text-brand-olive">
            <SafeIcon icon={FiChevronLeft} />
          </button>
          <span className="px-6 font-bold text-brand-dark uppercase tracking-widest text-xs min-w-[150px] text-center">
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-3 hover:bg-brand-cream text-brand-olive">
            <SafeIcon icon={FiChevronRight} />
          </button>
        </div>
      </div>
    </div>
  );

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const dateStr = format(cloneDay, 'yyyy-MM-dd');
        const isBlocked = blockedDates.includes(dateStr);
        const isCurrentMonth = isSameMonth(cloneDay, monthStart);
        const isToday = isSameDay(cloneDay, new Date());

        days.push(
          <div 
            key={dateStr}
            onClick={() => toggleDate(dateStr)}
            className={`
              relative h-24 border-r border-b border-brand-light/10 p-2 cursor-pointer transition-all
              ${!isCurrentMonth ? 'bg-brand-cream/20 text-brand-light' : 'bg-white text-brand-dark'}
              ${isBlocked ? 'bg-red-50' : 'hover:bg-brand-olive/5'}
            `}
          >
            <span className={`text-xs font-bold ${isToday ? 'text-brand-olive' : ''}`}>
              {format(cloneDay, 'd')}
            </span>
            <div className="mt-2 text-center">
              {isBlocked ? (
                <div className="inline-block bg-red-100 text-red-700 text-[8px] uppercase tracking-tighter font-bold px-2 py-1 rounded-sm">Booked</div>
              ) : (
                <div className="inline-block bg-green-50 text-green-600 text-[8px] uppercase tracking-tighter font-bold px-2 py-1 rounded-sm opacity-40">Available</div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(<div className="grid grid-cols-7" key={day.toString()}>{days}</div>);
      days = [];
    }

    return (
      <div className="bg-white rounded-sm border border-brand-light/30 shadow-sm overflow-hidden">
        <div className="grid grid-cols-7 bg-brand-cream/50 text-brand-sage text-[10px] uppercase font-bold tracking-widest border-b border-brand-light/30">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="py-4 text-center">{d}</div>)}
        </div>
        {rows}
      </div>
    );
  };

  return (
    <div className="flex bg-brand-cream min-h-screen">
      <Sidebar />
      <main className="flex-grow p-8">
        {renderHeader()}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3">{renderCalendar()}</div>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-sm border border-brand-light/30 shadow-sm">
              <h3 className="font-serif text-xl mb-4 text-brand-dark">Legend</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-red-50 border border-red-100 rounded-sm"></div>
                  <span className="text-xs uppercase tracking-widest text-brand-sage font-bold">Booked</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-white border border-brand-light/20 rounded-sm"></div>
                  <span className="text-xs uppercase tracking-widest text-brand-sage font-bold">Available</span>
                </div>
              </div>
            </div>
            <div className="bg-brand-olive text-brand-cream p-6 rounded-sm shadow-lg">
              <h4 className="font-serif text-lg mb-4 flex items-center gap-2">
                <SafeIcon icon={FiInfo} /> Admin Tip
              </h4>
              <p className="text-xs font-light leading-relaxed">Changes made here reflect instantly on the public website. Use this to block dates for maintenance or manual bookings.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AvailabilityManagement;